import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Paperclip, X, Loader2 } from 'lucide-react'
import { sendMessage, updateTyping, addArtifact } from '../../lib/session'
import { useToast } from '../common/Toast'
import { authHeaders } from '../../lib/auth'
import { FUNCTIONS_BASE_URL } from '../../lib/functionsBase'

interface MessageInputProps {
  sessionId: string
  participantId: string
  displayName: string
}

export default function MessageInput({ sessionId, participantId, displayName }: MessageInputProps) {
  const { addToast } = useToast()
  const [text, setText] = useState('')
  const [pastePopup, setPastePopup] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)
  const isTypingRef = useRef(false)

  const setTyping = useCallback(
    (typing: boolean) => {
      if (isTypingRef.current !== typing) {
        isTypingRef.current = typing
        updateTyping(sessionId, participantId, typing)
      }
    },
    [sessionId, participantId],
  )

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`
  }, [text])

  // Cleanup typing on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (isTypingRef.current) updateTyping(sessionId, participantId, false)
    }
  }, [sessionId, participantId])

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value)
    setTyping(true)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => setTyping(false), 2000)
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const pasted = e.clipboardData.getData('text')
    if (pasted.length > 500) {
      e.preventDefault()
      setPastePopup(pasted)
    }
  }

  async function handleSend() {
    const trimmed = text.trim()
    if (!trimmed || sending) return
    setSending(true)
    try {
      await sendMessage(sessionId, participantId, displayName, trimmed)
      setText('')
      setTyping(false)
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      textareaRef.current?.focus()
    } finally {
      setSending(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function toBase64(bytes: Uint8Array): string {
    // Chunked to avoid RangeError from spreading large arrays into fromCharCode
    let binary = ''
    const chunkSize = 8192
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)))
    }
    return btoa(binary)
  }

  async function processFile(file: File) {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp']

    let content: string
    let fileType = ext

    if (imageExts.includes(ext)) {
      // Images: store as base64 data URL for the vision API
      setUploading(true)
      try {
        const arrayBuffer = await file.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)
        // Base64 inflates by 4/3 and the whole artifact doc must stay under Firestore's 1MiB cap
        if (bytes.length > 700 * 1024) {
          addToast('That image is over the 700KB limit. Try a smaller one.', 'error')
          return
        }
        const base64 = toBase64(bytes)
        const mediaType = file.type || `image/${ext === 'jpg' ? 'jpeg' : ext}`
        content = `image:${mediaType}:${base64}`
        fileType = ext
      } catch {
        addToast(`Failed to process image ${file.name}`, 'error')
        return
      } finally {
        setUploading(false)
      }
    } else if (ext === 'txt') {
      content = await file.text()
    } else if (['pdf', 'docx', 'xlsx', 'xls', 'csv', 'pptx'].includes(ext)) {
      if (file.size > 10 * 1024 * 1024) {
        addToast('That file is over the 10MB limit. Try a smaller one.', 'error')
        return
      }
      setUploading(true)
      try {
        const arrayBuffer = await file.arrayBuffer()
        const base64 = toBase64(new Uint8Array(arrayBuffer))

        const res = await fetch(`${FUNCTIONS_BASE_URL}/extractText`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ sessionId, fileData: base64, fileType: file.type, fileName: file.name }),
        })

        if (!res.ok) {
          addToast(`Failed to extract text from ${file.name}`, 'error')
          return
        }
        const data = await res.json()
        content = data.text
      } catch {
        addToast(`Failed to extract text from ${file.name}`, 'error')
        return
      } finally {
        setUploading(false)
      }
    } else {
      content = await file.text()
    }

    try {
      await addArtifact(sessionId, displayName, file.name, content, fileType)
      await sendMessage(sessionId, participantId, displayName, file.name, 'artifact')
    } catch {
      addToast(`Failed to upload ${file.name}`, 'error')
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await processFile(file)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragging(true)
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    await processFile(file)
  }

  async function handlePasteAsMessage(pastedText: string) {
    setPastePopup(null)
    setText(pastedText)
  }

  async function handlePasteAsDocument(pastedText: string) {
    setPastePopup(null)
    // Derive a meaningful name from the first line of content
    const firstLine = pastedText.split('\n').find(l => l.trim().length > 0)?.trim() || 'pasted-text'
    const sanitized = firstLine
      .replace(/[^a-zA-Z0-9\s\-_]/g, '')
      .trim()
      .slice(0, 40)
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase()
    const fileName = `${sanitized || 'pasted-text'}-${Date.now()}.txt`
    await addArtifact(sessionId, displayName, fileName, pastedText, 'txt')
    await sendMessage(sessionId, participantId, displayName, fileName, 'artifact')
  }

  return (
    <div
      className={`bg-surface border-t-2 border-navy px-3 py-2 sm:px-4 sm:py-3 relative ${dragging ? 'outline-2 outline-amber -outline-offset-2' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {dragging && (
        <div className="absolute inset-0 bg-paper/90 flex items-center justify-center z-10 pointer-events-none">
          <span className="mono-label text-amber">Drop the file to upload</span>
        </div>
      )}

      {/* Upload progress indicator */}
      {uploading && (
        <div className="mb-2 bg-paper border-2 border-navy p-3 flex items-center gap-2 mono-label text-navy/75" role="status">
          <Loader2 className="w-4 h-4 text-navy animate-spin shrink-0" aria-hidden="true" />
          <span>Extracting text</span>
        </div>
      )}

      {/* Paste popup */}
      {pastePopup && (
        <div className="mb-2 bg-paper border-2 border-navy p-3 flex items-center justify-between gap-3 text-sm">
          <span className="text-navy/75">
            That is a long paste ({pastePopup.length.toLocaleString()} characters)
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => handlePasteAsMessage(pastePopup)}
              className="px-3 py-2 mono-label bg-navy text-stone border-2 border-navy hover:bg-navy-lifted transition-colors cursor-pointer"
            >
              Send as message
            </button>
            <button
              onClick={() => handlePasteAsDocument(pastePopup)}
              className="px-3 py-2 mono-label bg-amber text-white border-2 border-navy hover:bg-amber-dark transition-colors cursor-pointer"
            >
              Upload as document
            </button>
            <button
              onClick={() => setPastePopup(null)}
              className="p-1 text-navy/70 hover:text-navy cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* File upload */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="p-2 text-navy/70 hover:text-navy transition-colors shrink-0 cursor-pointer disabled:opacity-30 disabled:cursor-default"
          title="Upload a file"
        >
          <Paperclip className="w-5 h-5" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.xlsx,.xls,.csv,.pptx,.png,.jpg,.jpeg,.gif,.webp"
          className="hidden"
          onChange={handleFileUpload}
        />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Type your message"
          rows={1}
          className="flex-1 resize-none bg-paper border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-base outline-none placeholder:text-navy/70 max-h-[150px]"
        />

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!text.trim() || sending || uploading}
          className="p-2.5 bg-navy text-stone border-2 border-navy disabled:opacity-30 hover:bg-navy-lifted transition-colors shrink-0 cursor-pointer disabled:cursor-default"
          title="Send message"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
